// Creator Learning — grid of published modules + Getting Started checklist.
// Route: /creator/learning
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../auth/AuthContext";
import { BrandFooter } from "../components/brand/Footer";
import { LogoLockup } from "../components/brand/LogoLockup";

function CreatorNav({ onLogout, active }) {
  const link = (to, label, testid) => (
    <Link
      data-testid={testid}
      to={to}
      className={`transition-colors hover:text-brand-gold ${active === to ? "text-brand-gold" : "text-brand-text/50"}`}
    >
      {label}
    </Link>
  );
  return (
    <header className="border-b border-brand-text/10 bg-brand-bg/80 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-6">
          <LogoLockup variant="header" as={Link} to="/creator" />
          <nav className="hidden gap-6 text-[10px] uppercase tracking-[0.28em] md:flex">
            {link("/creator", "Dashboard", "nav-dashboard")}
            {link("/creator/learning", "Learning", "nav-learning")}
            {link("/profile", "My Profile", "nav-profile")}
            {link("/contract", "My Contract", "nav-contract")}
          </nav>
        </div>
        <button
          data-testid="nav-signout"
          onClick={onLogout}
          className="rounded-full border border-brand-text/15 px-4 py-2 text-[10px] uppercase tracking-[0.28em] text-brand-text/60 hover:border-brand-gold/40 hover:text-brand-gold"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

function ChecklistCard({ checklist, onToggle, busyKey }) {
  if (!checklist) return null;
  const { items, complete_count, total } = checklist;
  const percent = total ? Math.round((complete_count / total) * 100) : 0;
  return (
    <section data-testid="checklist-card" className="rounded-2xl border border-brand-text/10 bg-[#141414] p-6 sm:p-8">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Getting started</p>
        <p data-testid="checklist-progress" className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-text/60">
          {complete_count} of {total} complete
        </p>
      </div>
      <div className="mt-4 h-1 w-full overflow-hidden rounded bg-brand-text/10">
        <div
          data-testid="checklist-progress-bar"
          className="h-full bg-gradient-to-r from-brand-gold/70 to-brand-gold transition-all duration-700"
          style={{ width: `${percent}%` }}
        />
      </div>
      <ul className="mt-6 space-y-3">
        {items.map((it) => {
          const disabled = it.auto || busyKey === it.key;
          const boxCls = it.done
            ? "border-emerald-400/60 bg-emerald-400/20 text-emerald-300"
            : "border-brand-text/25 text-transparent";
          return (
            <li
              key={it.key}
              data-testid={`checklist-item-${it.key}`}
              data-done={it.done}
              className="flex items-start gap-3"
            >
              <button
                type="button"
                data-testid={`checklist-toggle-${it.key}`}
                aria-label={it.label}
                disabled={disabled}
                onClick={() => !disabled && onToggle(it.key, !it.done)}
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${boxCls} ${disabled ? "cursor-not-allowed opacity-70" : "hover:border-brand-gold/60"}`}
              >
                {it.done && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <div className="flex-1">
                <div className={`text-sm ${it.done ? "text-brand-text/80" : "text-brand-text"}`}>{it.label}</div>
                <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-brand-text/40">
                  {it.auto ? "Auto — derived from your profile" : "Manual"}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ModuleCard({ m }) {
  return (
    <Link
      to={`/creator/learning/${m.module_id}`}
      data-testid={`module-card-${m.module_id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-brand-text/10 bg-[#141414] transition hover:border-brand-gold/40"
    >
      <div className="relative h-32 overflow-hidden bg-gradient-to-br from-brand-gold/20 via-brand-bg to-brand-bg">
        {m.hero_image_url ? (
          <img src={m.hero_image_url} alt="" className="h-full w-full object-cover opacity-80 transition group-hover:opacity-100" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-display text-4xl text-brand-gold/40">Ω</span>
          </div>
        )}
        <span className="absolute left-3 top-3 rounded-full border border-brand-gold/40 bg-brand-bg/70 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-brand-gold backdrop-blur">
          {m.category_label}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="font-display text-lg text-brand-text">{m.title}</h3>
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-text/45">
          ~{m.estimated_read_minutes} min read
        </p>
        <div className="mt-auto pt-3">
          {m.read_at ? (
            <span data-testid={`module-read-badge-${m.module_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-300">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
              Read {new Date(m.read_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-gold/70">Open →</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CreatorLearning() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [checklist, setChecklist] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { document.title = "Learning · Omertàbella"; }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [modsRes, checkRes] = await Promise.all([
        apiClient.get("/learning/modules"),
        apiClient.get("/me/learning/checklist"),
      ]);
      setModules(modsRes.data.items);
      setCategories(modsRes.data.categories);
      setChecklist(checkRes.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onToggle = async (key, done) => {
    setBusyKey(key);
    try {
      await apiClient.post(`/me/learning/checklist/${key}`, { done });
      const r = await apiClient.get("/me/learning/checklist");
      setChecklist(r.data);
    } finally { setBusyKey(null); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return modules.filter((m) => {
      if (category !== "all" && m.category !== category) return false;
      if (q && !m.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [modules, category, search]);

  // Group visible modules by category (for the grouped grid).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const m of filtered) {
      if (!map.has(m.category)) map.set(m.category, []);
      map.get(m.category).push(m);
    }
    return Array.from(map.entries()).map(([cat, items]) => ({ cat, items }));
  }, [filtered]);

  const onSignOut = async () => { await logout(); navigate("/", { replace: true }); };

  return (
    <main data-testid="creator-learning" className="min-h-screen bg-brand-bg text-brand-text">
      <CreatorNav onLogout={onSignOut} active="/creator/learning" />

      <div className="mx-auto max-w-5xl px-6 py-12 sm:py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Learning Library</p>
        <h1 className="mt-3 font-display text-4xl text-brand-text sm:text-5xl lg:text-6xl">
          Play<span className="text-brand-gold">books</span>.
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-brand-text/60">
          Every module we've written for our roster creators. Start with the checklist, then work through the modules that match what you're building this week.
        </p>

        <div className="mt-10">
          <ChecklistCard checklist={checklist} onToggle={onToggle} busyKey={busyKey} />
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="filter-all"
              onClick={() => setCategory("all")}
              className={`rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] transition ${category === "all" ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-text/15 text-brand-text/60 hover:border-brand-gold/40"}`}
            >All</button>
            {categories.map((c) => (
              <button
                key={c.key}
                data-testid={`filter-${c.key}`}
                onClick={() => setCategory(c.key)}
                className={`rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] transition ${category === c.key ? "border-brand-gold bg-brand-gold/10 text-brand-gold" : "border-brand-text/15 text-brand-text/60 hover:border-brand-gold/40"}`}
              >{c.label}</button>
            ))}
          </div>
          <input
            data-testid="learning-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search modules…"
            className="w-full max-w-xs rounded-full border border-brand-text/15 bg-[#141414] px-4 py-2 text-sm text-brand-text placeholder:text-brand-text/30 focus:border-brand-gold focus:outline-none"
          />
        </div>

        {/* Grid, grouped by category */}
        <div className="mt-8 space-y-10">
          {loading && <div data-testid="learning-loading" className="py-16 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/45">Loading library…</div>}
          {!loading && filtered.length === 0 && (
            <div data-testid="learning-empty" className="rounded-2xl border border-brand-text/10 bg-[#141414] p-12 text-center text-sm text-brand-text/50">
              No modules match your filters.
            </div>
          )}
          {grouped.map(({ cat, items }) => (
            <section key={cat} data-testid={`learning-group-${cat}`}>
              <h2 className="mb-4 font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/50">
                {items[0]?.category_label || cat}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((m) => <ModuleCard key={m.module_id} m={m} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
      <BrandFooter />
    </main>
  );
}
